import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { Clear, PictureAsPdf } from "@mui/icons-material";
import {
  SheqPpeRecord,
  useGetSheqWorkerHoldingsQuery,
  useUpdateSheqPpeRecordMutation,
} from "../../../redux/sheqApi";
import { generatePpeSlip } from "./generatePpeSlip";

const NAVY = "#123456";

interface Props {
  open: boolean;
  record: SheqPpeRecord | null;
  onClose: () => void;
}

export const PpeSlipDialog: React.FC<Props> = ({ open, record, onClose }) => {
  const sigRef = useRef<SignatureCanvas>(null);
  const [sheqOfficer, setSheqOfficer] = useState("");
  const [generating, setGenerating] = useState(false);
  const [updateRecord] = useUpdateSheqPpeRecordMutation();

  // Load all active PPE for this worker so the slip is comprehensive
  const { data: holdings = [], isFetching } = useGetSheqWorkerHoldingsQuery(
    record?.workerId ?? "",
    { skip: !record?.workerId || !open },
  );

  const activeHoldings = holdings;
  const slipRecords = activeHoldings.length > 0 ? activeHoldings : record ? [record] : [];
  const itemCount = slipRecords.length;

  React.useEffect(() => {
    if (open) {
      setSheqOfficer(record?.issuedByName ?? "");
      // slight delay to let the canvas mount
      setTimeout(() => sigRef.current?.clear(), 80);
    }
  }, [open, record]);

  const handleGenerate = async () => {
    if (!record || slipRecords.length === 0) return;
    setGenerating(true);
    try {
      const sig = sigRef.current;
      // getTrimmedCanvas() is broken in react-signature-canvas 1.1.0-alpha
      // due to a bad trim-canvas ESM export — use getCanvas() instead.
      const signatureDataUrl = sig && !sig.isEmpty()
        ? sig.getCanvas().toDataURL("image/png")
        : undefined;

      // Fetch logo from public folder
      let logoDataUrl: string | undefined;
      try {
        const resp = await fetch("/logo192.png");
        const blob = await resp.blob();
        logoDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        // logo optional
      }

      await generatePpeSlip({
        records: slipRecords,
        signatureDataUrl,
        sheqOfficerName: sheqOfficer.trim() || record.issuedByName,
        logoDataUrl,
      });

      // Stamp acknowledgement on all active records in this slip
      const ackAt = new Date().toISOString();
      await Promise.allSettled(
        slipRecords
          .filter((r) => r.id && r.status === "ACTIVE" && !r.staffAcknowledgedAt)
          .map((r) => updateRecord({ id: r.id!, body: { staffAcknowledgedAt: ackAt } })),
      );

      onClose();
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ bgcolor: NAVY, color: "#fff", py: 1.5, fontSize: 15 }}
      >
        PPE Acknowledgement Slip
      </DialogTitle>

      <DialogContent>
        <Box pt={2} display="flex" flexDirection="column" gap={2.5}>
          {/* Worker summary */}
          {record && (
            <Box
              sx={{
                bgcolor: "#f0f4f8",
                borderRadius: 1.5,
                p: 1.5,
                border: "1px solid #d0dce8",
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                {record.workerName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[record.workerEmployeeNo, record.department, record.blockName]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
              <Box mt={0.75}>
                {isFetching ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={12} />
                    <Typography variant="caption">
                      Loading all holdings…
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Slip will include{" "}
                    <strong>{itemCount} active PPE item{itemCount !== 1 ? "s" : ""}</strong>{" "}
                    for this worker.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Divider />

          {/* SHEQ Officer name */}
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color={NAVY}
              display="block"
              mb={0.75}
            >
              SHEQ / ISSUING OFFICER NAME
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={sheqOfficer}
              onChange={(e) => setSheqOfficer(e.target.value)}
              placeholder="Name printed on the slip"
            />
          </Box>

          {/* Signature pad */}
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={0.75}
            >
              <Typography variant="caption" fontWeight={700} color={NAVY}>
                STAFF SIGNATURE
              </Typography>
              <Button
                size="small"
                startIcon={<Clear sx={{ fontSize: 14 }} />}
                onClick={() => sigRef.current?.clear()}
                sx={{ fontSize: 11, py: 0.25 }}
              >
                Clear
              </Button>
            </Box>
            <Box
              sx={{
                border: "1px solid #bbb",
                borderRadius: 1,
                bgcolor: "#fff",
                cursor: "crosshair",
                overflow: "hidden",
                lineHeight: 0,
              }}
            >
              <SignatureCanvas
                ref={sigRef}
                penColor="#000"
                canvasProps={{
                  width: 556,
                  height: 130,
                  style: { display: "block", width: "100%", height: 130 },
                }}
                backgroundColor="rgb(255,255,255)"
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Staff member signs above (mouse or touch). Leave blank to print
              with a blank signature line.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={generating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            generating ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <PictureAsPdf />
            )
          }
          onClick={handleGenerate}
          disabled={generating || isFetching || slipRecords.length === 0}
          sx={{ bgcolor: NAVY }}
        >
          {generating ? "Saving & generating…" : "Save & Print"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
